import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AuthShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const next = params.get("next");
  const safeNext = next && (next.startsWith("/api/oauth/authorize") || next.startsWith("/oauth/consent")) ? next : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      const dest = safeNext || location.state?.from || (user.setup?.completed ? "/dashboard" : "/setup");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(apiError(err, "Unable to sign in."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Sign in to MIA"
      subtitle="Manage your devices, integrations and privacy."
      footer={<>New to MIA? <Link to={safeNext ? `/signup?next=${encodeURIComponent(safeNext)}` : "/signup"} data-testid="goto-signup-link" className="text-primary hover:underline">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signin-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required autoComplete="email" data-testid="signin-email-input"
                 value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" required autoComplete="current-password" data-testid="signin-password-input"
                 value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="signin-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading} data-testid="signin-submit-button">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
